// Notification Service for BrownStone Jazz Club App
// Handles new event notifications with sound alerts

const LAST_SEEN_EVENTS_KEY = 'brownstone_last_seen_events';
const NOTIFICATION_ENABLED_KEY = 'brownstone_notifications_enabled';

// Jazz-inspired notification sound using Web Audio API
export const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create a pleasant jazz chord progression (Cmaj7 arpeggio)
    const notes = [261.63, 329.63, 392.00, 493.88]; // C4, E4, G4, B4
    const duration = 0.15;
    const gap = 0.08;
    
    notes.forEach((freq, index) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Use a warm sine wave for jazz feel
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);
      
      // Smooth envelope for pleasant sound
      const startTime = audioContext.currentTime + (index * gap);
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    });
    
    return true;
  } catch (error) {
    console.warn('Could not play notification sound:', error);
    return false;
  }
};

// Get last seen event IDs from localStorage
export const getLastSeenEventIds = () => {
  try {
    const stored = localStorage.getItem(LAST_SEEN_EVENTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Save seen event IDs to localStorage
export const saveSeenEventIds = (eventIds) => {
  try {
    localStorage.setItem(LAST_SEEN_EVENTS_KEY, JSON.stringify(eventIds));
  } catch (error) {
    console.warn('Could not save seen events:', error);
  }
};

// Check if notifications are enabled
export const areNotificationsEnabled = () => {
  try {
    const stored = localStorage.getItem(NOTIFICATION_ENABLED_KEY);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
};

// Toggle notifications
export const setNotificationsEnabled = (enabled) => {
  try {
    localStorage.setItem(NOTIFICATION_ENABLED_KEY, String(enabled));
  } catch (error) {
    console.warn('Could not save notification preference:', error);
  }
};

// Check for new events and return them
export const checkForNewEvents = (currentEvents) => {
  const lastSeenIds = getLastSeenEventIds();
  
  // If this is the first time (no stored IDs), save current and return empty
  if (lastSeenIds.length === 0) {
    const currentIds = currentEvents.map(e => e.id);
    saveSeenEventIds(currentIds);
    return [];
  }
  
  // Find events that weren't in the last seen list
  const newEvents = currentEvents.filter(event => !lastSeenIds.includes(event.id));
  
  // Update the stored IDs with current events
  const currentIds = currentEvents.map(e => e.id);
  saveSeenEventIds(currentIds);
  
  return newEvents;
};

// Request browser notification permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  
  if (Notification.permission === 'granted') {
    return 'granted';
  }
  
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }
  
  return Notification.permission;
};

// Show browser notification for new event
export const showBrowserNotification = (event) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return false;
  }
  
  try {
    const notification = new Notification('New Event at BrownStone Jazz Club!', {
      body: `${event.title} - ${event.date} at ${event.time}`,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: `event-${event.id}`,
      requireInteraction: false,
      silent: true // We'll play our own sound
    });
    
    notification.onclick = () => {
      window.focus();
      window.location.href = `/events/${event.id}`;
      notification.close();
    };
    
    return true;
  } catch (error) {
    console.warn('Could not show notification:', error);
    return false;
  }
};
